import { LabelChip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { stringify } from "yaml";
import type { JsonObject } from "@/api/common";
import type {
  ConfigReq,
  DatasetSelectionReq,
  IsaacEvaluationScenarioReq,
  RegistryDatasetRef,
  ResourcesReq,
  TrainingInitReq,
  TrainingSessionRecipeView,
} from "@/api/training";
import type {
  TrainingScheduleOperationsItem,
  TrainingScheduleRecipeView,
} from "@/api/trainingSchedule";
import { CodeEditor } from "@/components/codeEditor";
import { Fact } from "@/components/dataset/DatasetInfoFacts";
import { RightSidePanel } from "@/components/modal";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { RegistryDatasetRefLink } from "../RegistryDatasetRefLink";
import {
  allowSpotLabel,
  minLifetimeRequestLabel,
  sessionBranchLabel,
} from "../sessionDetail/trainingSessionDetailFormat";
import { isRandomBackgroundOverrideKey } from "../submit/recipe/recipeRandomBackground";
import { ScheduleRecipeScenarioTable } from "./ScheduleRecipeScenarioTable";
import { scheduleStatusChipColor, scheduleStatusLabel } from "./scheduleFormat";
import { useScheduleRecipe } from "./useScheduleRecipe";
import styles from "./ScheduleRecipePanel.module.css";

const EMPTY = "—";

/**
 * Schedule recipe 우측 패널. 목록의 이름 클릭이 연다. 마운트 = 열림이다. 그래서 SchedulePage 가
 * 조건부로 렌더하고, 닫으면 언마운트되어 받은 recipe 도 버린다. `schedule` 은 목록의 최신 행
 * (제목·상태 칩·Dataset 표시명)이고, recipe 는 열릴 때 한 번 받는다(§12).
 */
export function ScheduleRecipePanel({
  schedule,
  onClose,
}: {
  schedule: TrainingScheduleOperationsItem;
  onClose: () => void;
}): ReactNode {
  const { state, reload } = useScheduleRecipe(schedule.id);

  return (
    <RightSidePanel
      title={`${schedule.name} · Recipe`}
      width={{ kind: "flex", percent: 60 }}
      headerActions={
        <LabelChip color={scheduleStatusChipColor(schedule.status)} size="sm" variant="filled">
          {scheduleStatusLabel(schedule.status)}
        </LabelChip>
      }
      onBackdrop={onClose}
      onRequestClose={onClose}
    >
      {state.status === "success" ? (
        <RecipeBody view={state.data} datasetRefs={datasetRefsOf(schedule)} />
      ) : state.status === "error" ? (
        // 영구 실패는 사유만 표시한다. "잠시 후 다시" 와 버튼을 붙이면 안내가 사실과 다르다.
        <EmptyState
          tone="error"
          action={state.retryable ? { label: "다시 시도", onClick: reload } : undefined}
        >
          {state.retryable
            ? `Recipe를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요. ${state.message}`
            : state.message}
        </EmptyState>
      ) : (
        <LoadingState>Recipe를 불러오고 있습니다.</LoadingState>
      )}
    </RightSidePanel>
  );
}

// 복원된 생성 입력은 Dataset 을 UUID 로만 포함한다. 목록 요약은 같은 입력의 registry 참조를 이미
// 가진다. 그래서 표시명과 링크는 열기 시점 스냅샷에서 가져온다. 없으면 UUID 평문으로 표시한다.
function datasetRefsOf(schedule: TrainingScheduleOperationsItem): Map<string, RegistryDatasetRef> {
  const refs = new Map<string, RegistryDatasetRef>();
  for (const dataset of schedule.schedule_recipe?.datasets ?? []) {
    if (dataset.registry_ref) {
      refs.set(dataset.registry_ref.uuid, dataset.registry_ref);
    }
  }
  return refs;
}

function RecipeBody({
  view,
  datasetRefs,
}: {
  view: TrainingScheduleRecipeView;
  datasetRefs: Map<string, RegistryDatasetRef>;
}): ReactNode {
  const recipe = view.recipe;
  return (
    <>
      <RecipeCard title="기본 정보">
        <dl className={styles.facts}>
          <Fact label="Session 종류" value={recipe.session_type ?? EMPTY} />
          <Fact label="시작점" value={initLabel(recipe.init)} />
          <Fact
            label="learning-core"
            value={sessionBranchLabel({ learning_core: recipe.learning_core, config: {} })}
          />
          <Fact label="Session 이름" value={recipe.display_name ?? EMPTY} />
          {/* finetune 제출에는 무효라 BE 가 false 로 바꿔 저장한다. 저장된 값을 그대로 표시한다. */}
          <Fact
            label="재시도 시 이어서"
            value={recipe.resume_on_retry === true ? "예" : "아니오"}
          />
          <Fact label="제출 Schema 버전" value={`v${view.submit_schema_version}`} />
        </dl>
      </RecipeCard>
      <DatasetsSection
        datasets={recipe.datasets}
        targetIndex={recipe.target_dataset_index ?? null}
        refs={datasetRefs}
      />
      <ConfigSection config={recipe.config} />
      <EvalScenariosSection scenarios={recipe.isaac_evaluation?.scenarios ?? []} />
      <ResourcesSection resources={recipe.resources} />
    </>
  );
}

// `init` 이 없으면 scratch 다. BE 는 그 경우에만 키를 빼고 보낸다.
function initLabel(init: TrainingInitReq | null | undefined): string {
  if (init == null) {
    return "scratch";
  }
  if (init.strategy === "finetune_from_policy") {
    return `finetune_from_policy · ${init.policy_id}`;
  }
  return `finetune_from_checkpoint · ${init.source_session_id}`;
}

// 섹션 카드. 제목이 region 이름이므로 둘이 달라지지 않도록 한 곳에서 붙인다.
function RecipeCard({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className={styles.card} aria-label={title}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {children}
    </section>
  );
}

function DatasetsSection({
  datasets,
  targetIndex,
  refs,
}: {
  datasets: DatasetSelectionReq[];
  targetIndex: number | null;
  refs: Map<string, RegistryDatasetRef>;
}): ReactNode {
  return (
    <RecipeCard title="Dataset">
      {datasets.length === 0 ? (
        <EmptyState size="sm">지정한 Dataset이 없습니다.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">이름</th>
                <th scope="col">Role</th>
                <th scope="col">Weight</th>
                <th scope="col">Annotation set</th>
                <th scope="col">타깃</th>
              </tr>
            </thead>
            <tbody>
              {datasets.map((dataset, index) => (
                // 같은 Dataset 을 역할·비율만 바꿔 두 번 넣을 수 있다. 그래서 uuid 가 유일하지 않다.
                <tr key={index}>
                  <td>
                    <RegistryDatasetRefLink
                      registryRef={refs.get(dataset.dataset_uuid)}
                      upstreamLabel={dataset.dataset_uuid}
                    />
                  </td>
                  <td>{dataset.role}</td>
                  <td>{dataset.weight ?? EMPTY}</td>
                  <td className={styles.mono}>{dataset.annotation_set_uuid ?? EMPTY}</td>
                  <td>{index === targetIndex ? "타깃" : EMPTY}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </RecipeCard>
  );
}

function ConfigSection({ config }: { config: ConfigReq | undefined }): ReactNode {
  return (
    <RecipeCard title="Config">
      {config === undefined ? (
        <EmptyState size="sm">지정한 Config가 없습니다.</EmptyState>
      ) : (
        <ConfigBody config={config} />
      )}
    </RecipeCard>
  );
}

function ConfigBody({ config }: { config: ConfigReq }): ReactNode {
  const overrides = config.overrides ?? null;
  const overrideEntries = overrides === null ? [] : Object.entries(overrides);
  return (
    <>
      <dl className={styles.facts}>
        <Fact label="출처" value={config.source ?? EMPTY} />
        <Fact label="이름" value={config.name ?? EMPTY} />
        {/* 부재는 기록 없음이고 {} 는 명시적 0건이다. 둘을 같은 표기로 합치지 않는다. */}
        <Fact
          label="Overrides"
          value={overrides === null ? EMPTY : `${overrideEntries.length}건`}
        />
      </dl>
      {overrideEntries.length > 0 ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col" className={styles.keyColumn}>
                  Key
                </th>
                <th scope="col">값</th>
              </tr>
            </thead>
            <tbody>
              {overrideEntries.map(([key, value]) => (
                <tr key={key}>
                  <td>
                    <span className={styles.mono}>{key}</span>
                    {/* 패널에는 사용자가 직접 넣은 행과 구분할 원본 config 가 없다. 그래서 판정하지
                    않고 가능성만 알린다. */}
                    {isRandomBackgroundOverrideKey(key) ? (
                      <p className={styles.keyNote}>
                        생성 폼에서 RandomBackground를 끄면 자동으로 생기는 행일 수 있습니다.
                      </p>
                    ) : null}
                  </td>
                  <td className={styles.mono}>
                    {value !== null && typeof value === "object" ? (
                      <pre className={styles.jsonValue}>{JSON.stringify(value, null, 2)}</pre>
                    ) : (
                      jsonText(value)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {config.inline_yaml == null ? null : (
        <div className={styles.editor}>
          <CodeEditor
            value={config.inline_yaml}
            onChange={() => undefined}
            language="yaml"
            readOnly
            rows={20}
            ariaLabel="Schedule에 저장된 Config YAML"
          />
        </div>
      )}
    </>
  );
}

function EvalScenariosSection({
  scenarios,
}: {
  scenarios: IsaacEvaluationScenarioReq[];
}): ReactNode {
  return (
    <RecipeCard title="Eval scenario">
      {scenarios.length === 0 ? (
        <EmptyState size="sm">지정한 Eval scenario가 없습니다.</EmptyState>
      ) : (
        <ScheduleRecipeScenarioTable scenarios={scenarios} />
      )}
    </RecipeCard>
  );
}

function ResourcesSection({ resources }: { resources: ResourcesReq | undefined }): ReactNode {
  if (resources === undefined) {
    return null;
  }
  // 명시된 네 필드는 Fact 로 표시하고, registry 가 해석하지 않는 passthrough 만 YAML 로 남긴다. Spot·
  // 잔여 수명의 3-state 문구는 세션 상세와 같은 포맷터가 소유한다(두 포맷터는 config 를 읽지 않는다).
  const sessionRecipe: TrainingSessionRecipeView = { config: {}, resources };
  const extra: JsonObject = resources.extra ?? {};
  return (
    <RecipeCard title="Resources">
      <dl className={styles.facts}>
        <Fact label="Node" value={resources.nodes ?? EMPTY} />
        <Fact label="Node당 GPU" value={resources.gpus_per_node ?? EMPTY} />
        <Fact label="Spot 허용" value={allowSpotLabel(sessionRecipe)} />
        <Fact label="최소 잔여 수명" value={minLifetimeRequestLabel(sessionRecipe)} />
      </dl>
      {Object.keys(extra).length > 0 ? <pre className={styles.code}>{stringify(extra)}</pre> : null}
    </RecipeCard>
  );
}

function jsonText(value: unknown): string {
  if (value === undefined || value === null) {
    return EMPTY;
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}
